import { useRef, useState } from 'react';
import { CheckboxGlyph } from '../components/note/Checkbox';
import { Icon } from '../components/ui/Icon';
import { IconButton, Sheet } from '../components/ui/ui';
import { checklistProgress, lineOps, newLine } from '../notes/model';
import { TEMPLATES } from '../notes/templates';
import type { Line, Note, Priority } from '../storage/schemas';
import { store } from '../state/store';
import type { NoteEditing } from './useNoteEditing';

const PRIORITIES: Priority[] = ['none', 'low', 'medium', 'high'];
const PRIORITY_LABEL: Record<Priority, string> = { none: 'No priority', low: 'Low', medium: 'Medium', high: 'High' };

function toLocalInput(ts: number | null | undefined): string {
  if (!ts) return '';
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Organise checklist items: reorder (drag handle, buttons, Alt+↑/↓), due dates, priority, delete, progress, templates. */
export function ChecklistSheet({ open, onClose, note, editing, onScheduleItem }: { open: boolean; onClose: () => void; note: Note; editing: NoteEditing; onScheduleItem: (line: Line) => void }) {
  const [dragId, setDragId] = useState<string | null>(null);
  const listRef = useRef<HTMLOListElement>(null);
  const { done, total } = checklistProgress(note);
  const items = note.lines;

  const update = (id: string, patch: Partial<Line>, group = 'checklist') => editing.edit((n) => ({ ...n, lines: lineOps.update(n.lines, id, patch) }), group);

  const startDrag = (e: React.PointerEvent, id: string) => {
    e.preventDefault();
    setDragId(id);
    const list = listRef.current;
    if (!list) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    const move = (ev: PointerEvent) => {
      const rows = [...list.querySelectorAll<HTMLElement>('[data-line-id]')];
      const idx = rows.findIndex((r) => {
        const b = r.getBoundingClientRect();
        return ev.clientY < b.top + b.height / 2;
      });
      const target = idx === -1 ? rows.length - 1 : idx;
      const cur = store_lines().findIndex((l) => l.id === id);
      if (cur !== -1 && target !== cur && target !== cur + 1) editing.edit((n) => ({ ...n, lines: lineOps.moveTo(n.lines, id, target > cur ? target - 1 : target) }), `drag:${id}`);
    };
    const up = () => {
      setDragId(null);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  };
  // Read live lines during a drag (the `note` prop is from the render that started it).
  const store_lines = () => store.get().notes[note.id]?.lines ?? [];

  const addItem = () => editing.edit((n) => ({ ...n, lines: [...n.lines.filter((l) => l.text !== '' || n.lines.length > 1), newLine('', 'check')] }), 'structure');

  const applyTemplate = (key: string) => {
    const t = TEMPLATES.find((x) => x.id === key);
    if (!t) return;
    editing.edit(
      (n) => {
        const keep = n.lines.filter((l) => l.text.trim() !== '');
        return { ...n, lines: [...keep, ...t.lines.map((l) => ({ ...newLine(l.text, l.kind), marks: l.heading ? { heading: true } : undefined }))] };
      },
      'structure',
    );
  };

  return (
    <Sheet open={open} onClose={onClose} title="Checklist" tall>
      <div className="cl-pane">
        <div className="cl-progress" aria-live="polite">
          <div className="cl-bar" role="progressbar" aria-valuemin={0} aria-valuemax={total || 1} aria-valuenow={done} aria-label="Checklist progress">
            <span style={{ width: `${total ? (done / total) * 100 : 0}%` }} />
          </div>
          <span>
            {done} done · {total - done} left
          </span>
        </div>
        <ol ref={listRef} className="cl-list">
          {items.map((l, i) => (
            <li key={l.id} data-line-id={l.id} className={`cl-row ${dragId === l.id ? 'is-dragging' : ''} ${l.kind === 'check' ? '' : 'is-text'}`}>
              <span className="cl-grip" onPointerDown={(e) => startDrag(e, l.id)} aria-hidden="true" title="Drag to reorder">
                <Icon name="grip" size={20} />
              </span>
              {l.kind === 'check' ? (
                <button type="button" className="cl-check" role="checkbox" aria-checked={l.checked} aria-label={`Done: ${l.text || 'item'}`} onClick={() => {
                    update(l.id, { checked: !l.checked });
                    if (!l.checked) store.afterChecked(note.id, l.id);
                  }}>
                  <CheckboxGlyph checked={l.checked} color="#171717" />
                </button>
              ) : (
                <button type="button" className="cl-make-check" onClick={() => update(l.id, { kind: 'check' })} aria-label={`Make “${l.text || 'line'}” a checklist item`}>
                  <Icon name="plus" size={16} />
                </button>
              )}
              <input
                className="cl-text"
                value={l.text}
                onChange={(e) => update(l.id, { text: e.target.value }, `type:${l.id}`)}
                aria-label={`Item ${i + 1}`}
                onKeyDown={(e) => {
                  if (e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
                    e.preventDefault();
                    editing.edit((n) => ({ ...n, lines: lineOps.move(n.lines, l.id, e.key === 'ArrowUp' ? -1 : 1) }), 'structure');
                  }
                }}
              />
              {l.kind === 'check' && (
                <details className="cl-more">
                  <summary aria-label={`Options for ${l.text || 'item'}`}>
                    {l.priority && l.priority !== 'none' && <span className={`prio prio-${l.priority}`}>{l.priority[0].toUpperCase()}</span>}
                    {l.due ? <Icon name="alarm" size={18} /> : <Icon name="more" size={20} />}
                  </summary>
                  <div className="cl-menu">
                    <label>
                      Due
                      <input type="datetime-local" value={toLocalInput(l.due)} onChange={(e) => update(l.id, { due: e.target.value ? new Date(e.target.value).getTime() : null })} />
                    </label>
                    <label>
                      Priority
                      <select value={l.priority ?? 'none'} onChange={(e) => update(l.id, { priority: e.target.value as Priority })}>
                        {PRIORITIES.map((p) => (
                          <option key={p} value={p}>
                            {PRIORITY_LABEL[p]}
                          </option>
                        ))}
                      </select>
                    </label>
                    <button type="button" className="btn is-small" disabled={!l.due} onClick={() => onScheduleItem(l)}>
                      Remind me at due time
                    </button>
                  </div>
                </details>
              )}
              <span className="cl-move">
                <IconButton icon="back" label={`Move ${l.text || 'item'} up`} disabled={i === 0} onClick={() => editing.edit((n) => ({ ...n, lines: lineOps.move(n.lines, l.id, -1) }), 'structure')} className="rot-90" />
                <IconButton icon="forward" label={`Move ${l.text || 'item'} down`} disabled={i === items.length - 1} onClick={() => editing.edit((n) => ({ ...n, lines: lineOps.move(n.lines, l.id, 1) }), 'structure')} className="rot-90" />
              </span>
              <IconButton icon="trash" label={`Delete ${l.text || 'item'}`} onClick={() => editing.edit((n) => ({ ...n, lines: lineOps.remove(n.lines, l.id) }), 'structure')} />
            </li>
          ))}
        </ol>
        <div className="cl-actions">
          <button type="button" className="btn" onClick={addItem}>
            Add item
          </button>
          <button type="button" className="btn" disabled={done === 0} onClick={() => editing.edit((n) => ({ ...n, lines: n.lines.filter((l) => !(l.kind === 'check' && l.checked)).length ? n.lines.filter((l) => !(l.kind === 'check' && l.checked)) : [newLine()] }), 'structure')}>
            Clear completed
          </button>
          <button type="button" className="btn" disabled={done === 0} onClick={() => editing.edit((n) => ({ ...n, lines: n.lines.map((l) => (l.kind === 'check' ? { ...l, checked: false } : l)) }), 'structure')}>
            Uncheck all
          </button>
        </div>
        <h3 className="cl-subtitle">Add from a template</h3>
        <div className="chip-row cl-templates">
          {TEMPLATES.filter((t) => t.kind === 'checklist').map((t) => (
            <button key={t.id} type="button" className="chip" onClick={() => applyTemplate(t.id)}>
              {t.name}
            </button>
          ))}
        </div>
      </div>
    </Sheet>
  );
}
